import {
  Integration,
  IntegrationCapability,
  IntegrationType,
} from '@principle-theorem/integrations';
import {
  CampaignEntryPauseReason,
  ConditionLogicConfigurationCollection,
  IAppointment,
  IAutomationTiming,
  ICampaignEntryContext,
  IInvoice,
  IMarketingCampaign,
  IMarketingCampaignEntry,
  IMarketingCampaignEntryEngagement,
  IMarketingCampaignStep,
  IMarketingCampaignStepConversion,
  IPatient,
  ISMSIntegrationData,
  isRelativeTimingWithTime,
  MarketingCampaignCollection,
  MarketingCampaignEntryCollection,
  MarketingCampaignEntryLogEvent,
  MarketingCampaignEntryStatus,
  MarketingCampaignStepConversionStatus,
  MarketingCampaignStepConversionType,
  MarketingCampaignStepStatus,
  TemplateScope,
  TimingDirection,
} from '@principle-theorem/principle-core/interfaces';
import {
  all$,
  asReffable,
  asyncForEach,
  asyncReduce,
  AtLeast,
  collectionGroupQuery,
  CollectionReference,
  DayOfWeek,
  DAYS_OF_WEEK,
  DocumentReference,
  FieldValue,
  Firestore,
  IReffable,
  isSameRef,
  mergeDayAndTime,
  multiFilter,
  resolveWithConcurrency,
  subCollection,
  Timestamp,
  Timezone,
  toDayOfWeek,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  Transaction,
  uid,
  undeletedFilter,
  undeletedQuery,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { compact, isEqual, last, nth } from 'lodash';
import * as moment from 'moment-timezone';
import { Observable } from 'rxjs';
import {
  ConditionHandler,
  getConditionImplementations,
} from '../../condition-logic/condition-handler';
import { AutomationTiming } from '../automation/automation-timing';
import { Brand } from '../brand';
import { BillingMeter } from '../organisation/billing-meter';
import { Organisation } from '../organisation/organisation';
import { RecurrenceDirection } from '../schedule/recurrence-pattern';
import { MarketingCampaign } from './campaign';
import { CampaignCostEstimate } from './campaign-cost-estimate';
import { MarketingCampaignEntryEngagement } from './campaign-entry-engagement';
import { MarketingCampaignEntryLog } from './campaign-entry-log';
import {
  MarketingCampaignStepConversion,
  TranslateTriggerDateFn,
} from './campaign-step-conversion';

export class MarketingCampaignEntry {
  static init(
    overrides: AtLeast<
      IMarketingCampaignEntry,
      | 'patient'
      | 'campaignRef'
      | 'campaignRunRef'
      | 'scopeData'
      | 'scopeLabel'
      | 'steps'
    >
  ): IMarketingCampaignEntry {
    return {
      status: MarketingCampaignEntryStatus.Pending,
      deleted: false,
      hasDuplicateEmail: false,
      hasDuplicateMobile: false,
      brandRef: MarketingCampaign.brandRef(asReffable(overrides.campaignRef)),
      ...overrides,
      patient: toNamedDocument(overrides.patient),
      conversionUid: uid(),
    };
  }

  static campaignRef(
    entry: IReffable<IMarketingCampaignEntry>
  ): DocumentReference<IMarketingCampaign> {
    return Firestore.getParentDocRef<IMarketingCampaign>(entry.ref);
  }

  static campaign(
    entry: IReffable<IMarketingCampaignEntry>
  ): Promise<WithRef<IMarketingCampaign>> {
    return Firestore.getDoc(MarketingCampaignEntry.campaignRef(entry));
  }

  static stepConversionCol(
    entry: IReffable<IMarketingCampaignEntry>
  ): CollectionReference<IMarketingCampaignStepConversion> {
    return subCollection<IMarketingCampaignStepConversion>(
      entry.ref,
      MarketingCampaignEntryCollection.StepConversions
    );
  }

  static stepConversions$(
    entry: IReffable<IMarketingCampaignEntry>
  ): Observable<WithRef<IMarketingCampaignStepConversion>[]> {
    return all$(
      undeletedQuery(MarketingCampaignEntry.stepConversionCol(entry))
    );
  }

  static engagementCol(
    entry: IReffable<IMarketingCampaignEntry>
  ): CollectionReference<IMarketingCampaignEntryEngagement> {
    return subCollection<IMarketingCampaignEntryEngagement>(
      entry.ref,
      MarketingCampaignEntryCollection.Engagements
    );
  }

  static engagements$(
    entry: IReffable<IMarketingCampaignEntry>
  ): Observable<WithRef<IMarketingCampaignEntryEngagement>[]> {
    return all$(MarketingCampaignEntry.engagementCol(entry)).pipe(
      multiFilter(undeletedFilter)
    );
  }

  static async getCurrentStepConversion(
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<WithRef<IMarketingCampaignStepConversion> | undefined> {
    if (!entry.currentStep) {
      return;
    }
    return Firestore.getDoc(entry.currentStep);
  }

  static async getCurrentStep(
    entry: WithRef<IMarketingCampaignEntry>,
    transaction?: Transaction
  ): Promise<WithRef<IMarketingCampaignStep> | undefined> {
    const stepConversion =
      await MarketingCampaignEntry.getCurrentStepConversion(entry);

    if (!stepConversion) {
      return;
    }

    return Firestore.getDoc(stepConversion.stepRef, transaction);
  }

  static async getPreviousCompletedStepConversion(
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<WithRef<IMarketingCampaignStepConversion> | undefined> {
    if (!entry.currentStep) {
      return;
    }
    const currentStep = await Firestore.getDoc(entry.currentStep);
    const currentStepIndex = MarketingCampaignEntry.getStepIndex(
      entry,
      currentStep
    );

    const completedStatuses = [
      MarketingCampaignStepConversionStatus.Completed,
      MarketingCampaignStepConversionStatus.Converted,
      MarketingCampaignStepConversionStatus.Unenrolled,
      MarketingCampaignStepConversionStatus.Unsubscribed,
    ];

    if (currentStep && completedStatuses.includes(currentStep.status)) {
      return currentStep;
    }

    for (let i = currentStepIndex - 1; i >= 0; i--) {
      const stepRef = entry.steps[i];
      const stepConversion =
        await MarketingCampaignEntry.findStepConversionByStepRef(
          entry,
          stepRef
        );
      if (stepConversion && completedStatuses.includes(stepConversion.status)) {
        return stepConversion;
      }
    }
  }

  static async getNextStep(
    entry: WithRef<IMarketingCampaignEntry>,
    transaction?: Transaction
  ): Promise<WithRef<IMarketingCampaignStep> | undefined> {
    if (!entry.steps.length) {
      throw new Error('Campaign has no steps');
    }

    if (!entry.currentStep) {
      throw new Error('Current step not found in campaign');
    }

    const currentStep = await Firestore.getDoc(entry.currentStep);
    const currentStepIndex = MarketingCampaignEntry.getStepIndex(
      entry,
      currentStep
    );

    const nextStep = await asyncReduce<
      DocumentReference<IMarketingCampaignStep>,
      WithRef<IMarketingCampaignStep> | undefined
    >(
      entry.steps.slice(currentStepIndex + 1),
      async (lastStep, nextStepRef) => {
        if (lastStep) {
          return lastStep;
        }

        const step = await Firestore.getDoc(nextStepRef, transaction);
        if (step.deleted) {
          return;
        }

        return step;
      },
      undefined
    );

    return nextStep;
  }

  static async getLastStepConversion(
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<WithRef<IMarketingCampaignStepConversion> | undefined> {
    const lastStepRef = last(entry.steps);
    if (!lastStepRef) {
      return;
    }
    return MarketingCampaignEntry.findStepConversionByStepRef(
      entry,
      lastStepRef
    );
  }

  static async getMostRecentStepAt(
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<Timestamp | undefined> {
    const currentStep =
      await MarketingCampaignEntry.getCurrentStepConversion(entry);

    if (!currentStep) {
      return;
    }

    if (
      currentStep.status === MarketingCampaignStepConversionStatus.Completed
    ) {
      return currentStep.completedAt;
    }

    if (currentStep.status === MarketingCampaignStepConversionStatus.Failed) {
      return currentStep.failedAt;
    }

    return currentStep.updatedAt;
  }

  static async findStepConversionByStepRef(
    entry: IReffable<IMarketingCampaignEntry>,
    stepRef: DocumentReference<IMarketingCampaignStep>
  ): Promise<WithRef<IMarketingCampaignStepConversion> | undefined> {
    return Firestore.firstResult(
      undeletedQuery(MarketingCampaignEntry.stepConversionCol(entry)),
      where('stepRef', '==', stepRef)
    );
  }

  static async shouldQueueNextStep(
    entry: WithRef<IMarketingCampaignEntry>,
    stepConversion: WithRef<IMarketingCampaignStepConversion>
  ): Promise<boolean> {
    const currentStepConversion =
      await MarketingCampaignEntry.getCurrentStepConversion(entry);

    if (
      !currentStepConversion ||
      !isSameRef(stepConversion, currentStepConversion)
    ) {
      return false;
    }

    const isCurrentStepComplete =
      currentStepConversion.status &&
      [
        MarketingCampaignStepConversionStatus.Skipped,
        MarketingCampaignStepConversionStatus.Completed,
        MarketingCampaignStepConversionStatus.Failed,
      ].includes(currentStepConversion.status);

    const nextStepIsBlockedFromRunning = !isCurrentStepComplete;

    if (nextStepIsBlockedFromRunning) {
      return false;
    }

    return true;
  }

  static async skipRemainingSteps(
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<void> {
    const steps = await Firestore.getDocs(
      MarketingCampaignEntry.stepConversionCol(entry)
    );
    const incompleteStepStatuses = [
      MarketingCampaignStepConversionStatus.Pending,
      MarketingCampaignStepConversionStatus.Queued,
    ];
    const incompleteSteps = steps.filter(
      (step) =>
        step.deleted === false && incompleteStepStatuses.includes(step.status)
    );
    if (!incompleteSteps.length) {
      return;
    }
    await resolveWithConcurrency(incompleteSteps, 5, async (step) => {
      await Firestore.patchDoc(step.ref, {
        status: MarketingCampaignStepConversionStatus.Skipped,
        skippedReason: 'Campaign Entry finished early',
      });
    });
    const lastStep = await MarketingCampaignEntry.getLastStepConversion(entry);
    await Firestore.patchDoc(entry.ref, {
      currentStep: lastStep?.ref,
    });
  }

  static async evaluateStepConditionsForEntry(
    requiredConditions: ConditionLogicConfigurationCollection,
    entryContext: ICampaignEntryContext
  ): Promise<boolean> {
    const handler = new ConditionHandler(getConditionImplementations());
    return handler.passesForCampaignEntry(requiredConditions, entryContext);
  }

  /**
   * Calculates the estimated date a campaign step will run based on its
   * automation timing configuration. Applies day-of-week constraints,
   * relative timing offsets, and optional time-of-day overrides from the
   * given base date (defaults to now).
   */
  static calculateEstimatedRunDate(
    timing: IAutomationTiming,
    baseDate?: Timestamp,
    timezone?: Timezone
  ): Timestamp {
    if (timing.direction === TimingDirection.Immediately) {
      return baseDate ?? toTimestamp(moment());
    }

    const base = baseDate ? moment(baseDate.toDate()) : moment();

    const baseMoment =
      isRelativeTimingWithTime(timing) && timezone
        ? mergeDayAndTime(toMomentTz(base, timezone), timing.time, timezone)
        : base;

    const estimatedRunMoment = AutomationTiming.getNextValidOccurrence(
      baseMoment,
      timing.daysOfWeek,
      RecurrenceDirection.Forward,
      timezone ?? Timezone.AustraliaSydney,
      timing.countOnlySelectedDays ?? false,
      timing.unit,
      timing.amount
    );

    return toTimestamp(estimatedRunMoment);
  }

  /**
   * If a estimatedRunDate falls in the past relative to `now`, slide it forward
   * to the next valid day (per daysOfWeek rules) while preserving the
   * time-of-day from the original forecast.
   *
   * Used after resume to ensure that forecast dates calculated from a
   * historical base (e.g. last completed step) land in the future.
   */
  static adjustEstimatedRunDateToFuture(
    estimatedRunDate: Timestamp,
    now: Timestamp,
    daysOfWeek: DayOfWeek[] | undefined,
    timezone: Timezone
  ): Timestamp {
    const estimatedRunMoment = toMomentTz(estimatedRunDate, timezone);
    const nowMoment = toMomentTz(now, timezone);

    if (estimatedRunMoment.isSameOrAfter(nowMoment)) {
      return estimatedRunDate;
    }

    const timeOfDay = {
      hour: estimatedRunMoment.hour(),
      minute: estimatedRunMoment.minute(),
      second: estimatedRunMoment.second(),
    };

    const candidate = nowMoment.clone().set(timeOfDay);

    if (candidate.isSameOrBefore(nowMoment)) {
      candidate.add(1, 'day');
    }

    const hasCustomDaysOfWeek =
      !!daysOfWeek &&
      daysOfWeek.length > 0 &&
      !isEqual(daysOfWeek, DAYS_OF_WEEK);

    if (hasCustomDaysOfWeek) {
      while (!daysOfWeek.includes(toDayOfWeek(candidate, timezone))) {
        candidate.add(1, 'day');
      }
    }

    return toTimestamp(candidate);
  }

  static async addStep(
    entry: WithRef<IMarketingCampaignEntry>,
    step: WithRef<IMarketingCampaignStep>,
    transaction?: Transaction,
    entryContext?: ICampaignEntryContext,
    previousEstimatedRunDate?: Timestamp,
    timezone?: Timezone
  ): Promise<{ estimatedCost: number; estimatedRunDate: Timestamp }> {
    const estimate = await CampaignCostEstimate.estimateForStep(step);

    const campaignRef = MarketingCampaignEntry.campaignRef(entry);
    const brandRef = MarketingCampaign.brandRef(asReffable(campaignRef));
    const organisationRef = Brand.organisationRef(asReffable(brandRef));
    const organisation = await Firestore.getDoc(organisationRef);

    const smsIntegrations =
      await Integration.getByCapability<ISMSIntegrationData>(
        Organisation.integrationCol(organisation),
        IntegrationCapability.SMS
      );
    const smsProvider =
      organisation.integrations?.smsProvider ?? IntegrationType.Twilio;
    const integration = smsIntegrations.find((smsIntegration) => smsIntegration.type === smsProvider);

    if (!integration?.data.marketingPriceId) {
      // eslint-disable-next-line no-console
      console.error(
        `No pricing for integration found for organisation: ${organisation.ref.id}`
      );
    }

    const cost = integration?.data.marketingPriceId
      ? BillingMeter.resolveCostByLookupId(integration.data.marketingPriceId)
      : 0;

    let estimatedCost = CampaignCostEstimate.estimateCostAmount(estimate, cost);

    if (entryContext && step.config.requiredConditions) {
      const conditionsPassed =
        await MarketingCampaignEntry.evaluateStepConditionsForEntry(
          step.config.requiredConditions,
          entryContext
        );
      if (!conditionsPassed) {
        estimatedCost = 0;
      }
    }

    const estimatedRunDate = MarketingCampaignEntry.calculateEstimatedRunDate(
      step.config.timing,
      previousEstimatedRunDate,
      timezone
    );

    await Firestore.addDoc(
      MarketingCampaignEntry.stepConversionCol(entry),
      MarketingCampaignStepConversion.init({
        campaignRef,
        campaignRunRef: entry.campaignRunRef,
        stepRef: step.ref,
        status: MarketingCampaignStepConversionStatus.Pending,
        incurredCost: 0,
        estimatedCost,
        estimatedCostRemaining: estimatedCost,
        estimatedSegments: estimate.smsSegments,
        estimatedRunDate,
      }),
      undefined,
      transaction
    );

    return { estimatedCost, estimatedRunDate };
  }

  /**
   * Recalculates estimatedRunDate on all pending step conversions for an entry,
   * chaining each step's forecast from the previous one. Called when an entry
   * is resumed after being paused so forecast dates reflect the new timeline.
   *
   * Non-pending steps (already completed/failed/etc.) are not patched but
   * their automation triggerDate (or estimatedRunDate) is used as the chain base
   * for the next step, so the timing offsets stay correct.
   */
  static async recalculateEstimatedRunDates(
    entry: WithRef<IMarketingCampaignEntry>,
    baseDate: Timestamp,
    now: Timestamp,
    timezone?: Timezone
  ): Promise<void> {
    const allStepConversions = (
      await Firestore.getDocs(MarketingCampaignEntry.stepConversionCol(entry))
    ).filter(undeletedFilter);

    const orderedConversions = compact(
      entry.steps.map((stepRef) =>
        allStepConversions.find((conversion) =>
          isSameRef(conversion.stepRef, stepRef)
        )
      )
    );

    const pendingStatuses = [
      MarketingCampaignStepConversionStatus.Pending,
      MarketingCampaignStepConversionStatus.Queued,
    ];

    let previousEstimatedRunDate = baseDate;

    for (const conversion of orderedConversions) {
      if (!pendingStatuses.includes(conversion.status)) {
        const chainDate =
          await MarketingCampaignEntry._resolveStepChainDate(conversion);
        if (chainDate) {
          previousEstimatedRunDate = chainDate;
        }
        continue;
      }

      const step = await Firestore.getDoc(conversion.stepRef);
      const rawEstimatedRunDate = MarketingCampaignEntry.calculateEstimatedRunDate(
        step.config.timing,
        previousEstimatedRunDate,
        timezone
      );

      const estimatedRunDate = MarketingCampaignEntry.adjustEstimatedRunDateToFuture(
        rawEstimatedRunDate,
        now,
        step.config.timing.daysOfWeek,
        timezone ?? Timezone.AustraliaSydney
      );

      await Firestore.patchDoc(conversion.ref, { estimatedRunDate });
      previousEstimatedRunDate = estimatedRunDate;
    }
  }

  /**
   * Clears estimatedRunDate on all incomplete (Pending/Queued) step conversions
   * for an entry. Called when an entry is paused so that stale forecast dates
   * are not used in cost forecasting queries.
   */
  static async clearEstimatedRunDates(
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<void> {
    const allStepConversions = (
      await Firestore.getDocs(MarketingCampaignEntry.stepConversionCol(entry))
    ).filter(undeletedFilter);

    const incompleteStatuses = [
      MarketingCampaignStepConversionStatus.Pending,
      MarketingCampaignStepConversionStatus.Queued,
    ];

    const incompleteConversions = allStepConversions.filter((conversion) =>
      incompleteStatuses.includes(conversion.status)
    );

    await asyncForEach(incompleteConversions, (conversion) =>
      Firestore.patchDoc(conversion.ref, {
        estimatedRunDate: FieldValue.delete(),
      })
    );
  }

  static async queueNextStep(
    entry: WithRef<IMarketingCampaignEntry>,
    translateTriggerDateFn?: TranslateTriggerDateFn
  ): Promise<void> {
    if (!entry.steps.length) {
      throw new Error('Campaign has no steps');
    }

    const currentStep = entry.currentStep
      ? await Firestore.getDoc(entry.currentStep)
      : undefined;
    const shouldQueueNext = currentStep
      ? MarketingCampaignEntry.shouldQueueNextStep(entry, currentStep)
      : true;
    if (!shouldQueueNext) {
      return;
    }

    const nextStepRef = await MarketingCampaignEntry.getNextStepRef(entry);
    if (!nextStepRef) {
      // eslint-disable-next-line no-console
      console.log(`No next campaign step: ${entry.campaignRef.path}`);
      return;
    }
    const campaignStep = await Firestore.getDoc(nextStepRef);
    const nextStep = await MarketingCampaignEntry.findStepConversionByStepRef(
      entry,
      nextStepRef
    );

    if (!nextStep) {
      // eslint-disable-next-line no-console
      console.log(`No further steps in campaign: ${entry.campaignRef.path}`);
      return;
    }

    if (campaignStep.status === MarketingCampaignStepStatus.Draft) {
      await Firestore.patchDoc(entry.ref, {
        currentStep: nextStep.ref,
      });
      await Firestore.patchDoc(nextStep.ref, {
        status: MarketingCampaignStepConversionStatus.Skipped,
        skippedReason: 'Campaign Step is not active',
      });
      return;
    }

    await MarketingCampaignStepConversion.queueAutomation(
      nextStep,
      translateTriggerDateFn
    );
    await Firestore.patchDoc(entry.ref, {
      currentStep: nextStep.ref,
    });
  }

  static getStepIndex(
    entry: WithRef<IMarketingCampaignEntry>,
    step?: WithRef<IMarketingCampaignStepConversion>
  ): number {
    if (!step) {
      return -1;
    }
    return entry.steps.findIndex((stepRef) => isSameRef(stepRef, step.stepRef));
  }

  static async getNextStepRef(
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<DocumentReference<IMarketingCampaignStep> | undefined> {
    const currentStep =
      await MarketingCampaignEntry.getCurrentStepConversion(entry);
    const currentStepIndex = MarketingCampaignEntry.getStepIndex(
      entry,
      currentStep
    );
    return nth(entry.steps, currentStepIndex + 1);
  }

  static async shouldCompleteEntry(
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<boolean> {
    const currentStep =
      await MarketingCampaignEntry.getCurrentStepConversion(entry);
    if (!currentStep) {
      return false;
    }

    if (MarketingCampaignEntry.isFinalised(entry)) {
      return false;
    }

    if (
      currentStep.status === MarketingCampaignStepConversionStatus.Converted
    ) {
      return true;
    }

    const nextStep = await MarketingCampaignEntry.getNextStep(entry);
    if (nextStep) {
      return false;
    }

    const currentStepComplete = [
      MarketingCampaignStepConversionStatus.Completed,
      MarketingCampaignStepConversionStatus.Converted,
      MarketingCampaignStepConversionStatus.Failed,
      MarketingCampaignStepConversionStatus.Skipped,
    ].includes(currentStep.status);

    if (currentStepComplete) {
      return true;
    }

    return false;
  }

  static async completeEntry(
    entry: IReffable<IMarketingCampaignEntry>
  ): Promise<void> {
    await Firestore.patchDoc(entry.ref, {
      status: MarketingCampaignEntryStatus.Completed,
    });
    await MarketingCampaignEntryLog.createEntryLog(
      entry,
      MarketingCampaignEntryLogEvent.StatusChange,
      { message: 'Entry completed' }
    );
  }

  static async findStepToConvert(
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<WithRef<IMarketingCampaignStepConversion> | undefined> {
    const previousCompletedStep =
      await MarketingCampaignEntry.getPreviousCompletedStepConversion(entry);
    if (previousCompletedStep) {
      return previousCompletedStep;
    }
    const currentStep =
      await MarketingCampaignEntry.getCurrentStepConversion(entry);
    if (currentStep) {
      return currentStep;
    }
    return MarketingCampaignEntry.getLastStepConversion(entry);
  }

  static async convertEntry(
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<void> {
    if (entry.status === MarketingCampaignEntryStatus.Converted) {
      return;
    }
    await Firestore.patchDoc(entry.ref, {
      status: MarketingCampaignEntryStatus.Converted,
    });
    await MarketingCampaignEntryLog.createEntryLog(
      entry,
      MarketingCampaignEntryLogEvent.StatusChange,
      { message: 'Entry converted' }
    );
  }

  static async trackConversionById(
    conversionUid: string,
    conversionType: MarketingCampaignStepConversionType,
    details?: string
  ): Promise<void> {
    const campaignEntry = await Firestore.firstResult(
      collectionGroupQuery<IMarketingCampaignEntry>(
        MarketingCampaignCollection.MarketingCampaignEntries,
        where('conversionUid', '==', conversionUid.trim())
      )
    );

    if (!campaignEntry) {
      return;
    }

    const stepConversion =
      await MarketingCampaignEntry.findStepToConvert(campaignEntry);
    if (!stepConversion) {
      return;
    }

    const campaign = await Firestore.getDoc(
      MarketingCampaignEntry.campaignRef(campaignEntry)
    );

    await Firestore.addDoc(
      MarketingCampaignEntry.engagementCol(campaignEntry),
      MarketingCampaignEntryEngagement.init({
        patientRef: campaignEntry.patient.ref,
        campaignRef: campaign.ref,
        campaignRunRef: campaignEntry.campaignRunRef,
        conversionType,
        details,
      })
    );

    if (campaign.conversionType !== conversionType) {
      // eslint-disable-next-line no-console
      console.log(
        `Campaign conversion type does not match: ${campaign.conversionType} !== ${conversionType}`
      );
      return;
    }

    if (
      stepConversion.status === MarketingCampaignStepConversionStatus.Converted
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `Conversion already tracked for conversionUid: ${conversionUid}`
      );
      return;
    }

    // eslint-disable-next-line no-console
    console.log(
      `Tracking conversion for entry: ${campaignEntry.ref.path}, conversionUid: ${conversionUid}, type: ${conversionType}`
    );

    await Firestore.patchDoc(stepConversion.ref, {
      status: MarketingCampaignStepConversionStatus.Converted,
      convertedAt: toTimestamp(),
    });
    await MarketingCampaignEntryLog.createEntryLog(
      campaignEntry,
      MarketingCampaignEntryLogEvent.StatusChange,
      {
        message: `Conversion tracked (${conversionType})`,
      }
    );
  }

  static async trackConversionByPatient(
    patient: IReffable<IPatient>,
    conversionType: MarketingCampaignStepConversionType,
    details?: string
  ): Promise<void> {
    const entries = await Firestore.getDocs(
      collectionGroupQuery<IMarketingCampaignEntry>(
        MarketingCampaignCollection.MarketingCampaignEntries,
        where('patient.ref', '==', patient.ref)
      )
    );

    const matchingEntries = compact(
      await asyncForEach(entries, async (entry) => {
        const campaign = await Firestore.getDoc(entry.campaignRef);
        if (campaign.conversionType !== conversionType) {
          return;
        }

        if (
          ![
            MarketingCampaignEntryStatus.Active,
            MarketingCampaignEntryStatus.Completed,
          ].includes(entry.status)
        ) {
          return;
        }

        return entry;
      })
    );

    if (!matchingEntries.length) {
      return;
    }

    await asyncForEach(matchingEntries, async (campaignEntry) => {
      await MarketingCampaignEntry.trackConversionById(
        campaignEntry.conversionUid,
        conversionType,
        details
      );
    });
  }

  static async convertAtCurrentStep(
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<void> {
    const stepConversion =
      await MarketingCampaignEntry.findStepToConvert(entry);
    if (!stepConversion) {
      return;
    }
    await Firestore.patchDoc(stepConversion.ref, {
      status: MarketingCampaignStepConversionStatus.Converted,
      convertedAt: toTimestamp(),
      conversionType: MarketingCampaignStepConversionType.Manual,
    });

    await Firestore.patchDoc(entry.ref, {
      status: MarketingCampaignEntryStatus.Converted,
    });
  }

  static async unsubscribe(
    entry: WithRef<IMarketingCampaignEntry>,
    unsubscribedAt: Timestamp = toTimestamp()
  ): Promise<void> {
    // Step conversion patch is conditional — entries without step conversions
    // (e.g. Preview entries) won't have a step to convert. Entry status patch
    // always runs. This mirrors the unenrol() pattern.
    const currentStep =
      await MarketingCampaignEntry.getCurrentStepConversion(entry);
    if (currentStep) {
      await Firestore.patchDoc(currentStep.ref, {
        status: MarketingCampaignStepConversionStatus.Unsubscribed,
        unsubscribedAt,
      });
    }

    await Firestore.patchDoc(entry.ref, {
      status: MarketingCampaignEntryStatus.Unsubscribed,
    });
  }

  static async unenrol(entry: WithRef<IMarketingCampaignEntry>): Promise<void> {
    const currentStep =
      await MarketingCampaignEntry.getCurrentStepConversion(entry);
    if (currentStep) {
      await Firestore.patchDoc(currentStep.ref, {
        status: MarketingCampaignStepConversionStatus.Unenrolled,
      });
    }

    await Firestore.patchDoc(entry.ref, {
      status: MarketingCampaignEntryStatus.Unenrolled,
    });
  }

  static async pause(
    entry: WithRef<IMarketingCampaignEntry>,
    reason: CampaignEntryPauseReason
  ): Promise<void> {
    if (entry.status !== MarketingCampaignEntryStatus.Active) {
      throw new Error(
        `Cannot pause entry: entry is not active (current status: ${entry.status})`
      );
    }

    const status =
      reason === CampaignEntryPauseReason.InsufficientFunds
        ? MarketingCampaignEntryStatus.PausedForPayment
        : MarketingCampaignEntryStatus.Paused;

    await Firestore.patchDoc(entry.ref, {
      status,
      pauseReason: reason,
      pausedAt: toTimestamp(),
    });
  }

  static async resume(entry: WithRef<IMarketingCampaignEntry>): Promise<void> {
    if (
      entry.status !== MarketingCampaignEntryStatus.Paused &&
      entry.status !== MarketingCampaignEntryStatus.PausedForPayment
    ) {
      throw new Error(
        `Cannot resume entry: entry is not paused (current status: ${entry.status})`
      );
    }

    await Firestore.patchDoc(entry.ref, {
      status: MarketingCampaignEntryStatus.Active,
      resumedAt: toTimestamp(),
      pauseReason: FieldValue.delete(),
      pausedAt: FieldValue.delete(),
      billingAlertAcknowledged: FieldValue.delete(),
    });
  }

  static async restart(entry: WithRef<IMarketingCampaignEntry>): Promise<void> {
    if (entry.status !== MarketingCampaignEntryStatus.Converted) {
      throw new Error(
        `Cannot restart entry: entry is not converted (current status: ${entry.status})`
      );
    }

    await MarketingCampaignEntry._resetStepsForResumption(entry, [
      MarketingCampaignStepConversionStatus.Converted,
    ]);

    await Firestore.patchDoc(entry.ref, {
      status: MarketingCampaignEntryStatus.Pending,
    });
  }

  static async reenrol(entry: WithRef<IMarketingCampaignEntry>): Promise<void> {
    if (entry.status !== MarketingCampaignEntryStatus.Unenrolled) {
      throw new Error(
        `Cannot re-enrol entry: entry is not unenrolled (current status: ${entry.status})`
      );
    }

    await MarketingCampaignEntry._resetStepsForResumption(entry, [
      MarketingCampaignStepConversionStatus.Unenrolled,
    ]);

    await Firestore.patchDoc(entry.ref, {
      status: MarketingCampaignEntryStatus.Pending,
    });
  }

  static isPaused(entry: { status: MarketingCampaignEntryStatus }): boolean {
    return (
      entry.status === MarketingCampaignEntryStatus.Paused ||
      entry.status === MarketingCampaignEntryStatus.PausedForPayment
    );
  }

  static isActive(entry: WithRef<IMarketingCampaignEntry>): boolean {
    return (
      entry.status !== MarketingCampaignEntryStatus.Preview &&
      entry.status !== MarketingCampaignEntryStatus.Completed &&
      !MarketingCampaignEntry.isPaused(entry) &&
      !MarketingCampaignEntry.isFinalised(entry)
    );
  }

  static isFinalised(entry: WithRef<IMarketingCampaignEntry>): boolean {
    return (
      entry.status === MarketingCampaignEntryStatus.Converted ||
      entry.status === MarketingCampaignEntryStatus.Unenrolled ||
      entry.status === MarketingCampaignEntryStatus.Unsubscribed
    );
  }

  static async fromInvoice(
    invoice: IReffable<IInvoice>
  ): Promise<WithRef<IMarketingCampaignEntry>[]> {
    return Firestore.getDocs(
      collectionGroupQuery<IMarketingCampaignEntry>(
        MarketingCampaignCollection.MarketingCampaignEntries,
        where('scopeData.scope', '==', TemplateScope.Invoice),
        where('scopeData.invoiceRef', '==', invoice.ref)
      )
    );
  }

  static async fromAppointment(
    appointment: IReffable<IAppointment>
  ): Promise<WithRef<IMarketingCampaignEntry>[]> {
    return Firestore.getDocs(
      collectionGroupQuery<IMarketingCampaignEntry>(
        MarketingCampaignCollection.MarketingCampaignEntries,
        where('scopeData.scope', '==', TemplateScope.Appointment),
        where('scopeData.appointmentRef', '==', appointment.ref)
      )
    );
  }

  static async acknowledgePaymentAlerts(
    entries: WithRef<IMarketingCampaignEntry>[]
  ): Promise<void> {
    const unacknowledged = entries.filter(
      (entry) =>
        entry.status === MarketingCampaignEntryStatus.PausedForPayment &&
        !entry.billingAlertAcknowledged
    );
    await asyncForEach(unacknowledged, async (entry) => {
      await Firestore.patchDoc(entry.ref, {
        billingAlertAcknowledged: true,
      });
    });
  }

  /**
   * Reset steps after a trigger event (Unenrolled / Converted) so the
   * entry can be re-processed.
   *
   * Trigger steps and all downstream Skipped/Cancelled steps are reset
   * to Pending. currentStep is set to the step immediately before the
   * first trigger step (or cleared if the trigger is the first step)
   * so that queueNextStep picks up the trigger step itself.
   */
  private static async _resetStepsForResumption(
    entry: WithRef<IMarketingCampaignEntry>,
    triggerStatuses: MarketingCampaignStepConversionStatus[]
  ): Promise<void> {
    const allStepConversions = await Firestore.getDocs(
      MarketingCampaignEntry.stepConversionCol(entry)
    );

    const orderedStepConversions = compact(
      entry.steps.map((stepRef) =>
        allStepConversions.find(
          (conversion) =>
            conversion.deleted === false &&
            isSameRef(conversion.stepRef, stepRef)
        )
      )
    );

    const firstTriggerIndex = orderedStepConversions.findIndex((conversion) =>
      triggerStatuses.includes(conversion.status)
    );

    if (firstTriggerIndex === -1) {
      return;
    }

    const statusesToReset = [
      ...triggerStatuses,
      MarketingCampaignStepConversionStatus.Skipped,
      MarketingCampaignStepConversionStatus.Cancelled,
    ];

    const stepsToReset = orderedStepConversions.filter(
      (conversion, index) =>
        statusesToReset.includes(conversion.status) &&
        index >= firstTriggerIndex
    );

    if (!stepsToReset.length) {
      return;
    }

    // Reset trigger steps and downstream skipped/cancelled steps to Pending
    await resolveWithConcurrency(stepsToReset, 5, async (step) => {
      if (step.automationRef) {
        await MarketingCampaignStepConversion.cancelAutomation(step);
      }
      await Firestore.patchDoc(step.ref, {
        status: MarketingCampaignStepConversionStatus.Pending,
        automationRef: FieldValue.delete(),
        skippedReason: FieldValue.delete(),
        cancelledReason: FieldValue.delete(),
        convertedAt: FieldValue.delete(),
        conversionType: FieldValue.delete(),
        unsubscribedAt: FieldValue.delete(),
      });
    });

    // Set currentStep to the step before the first trigger step so
    // queueNextStep picks up the trigger step as "next".
    const resumeFromStep =
      firstTriggerIndex > 0
        ? orderedStepConversions[firstTriggerIndex - 1]
        : undefined;

    await Firestore.patchDoc(entry.ref, {
      currentStep: resumeFromStep?.ref ?? FieldValue.delete(),
    });
  }

  /**
   * Resolve the date that should be used as the chain base from a non-pending
   * step conversion. Prefers the automation's triggerDate (the actual scheduled
   * time), falling back to the conversion's estimatedRunDate.
   */
  private static async _resolveStepChainDate(
    conversion: WithRef<IMarketingCampaignStepConversion>
  ): Promise<Timestamp | undefined> {
    if (conversion.automationRef) {
      const automation = await Firestore.getDoc(conversion.automationRef);
      if (automation.triggerDate) {
        return automation.triggerDate;
      }
    }
    return conversion.estimatedRunDate;
  }
}
